import React, { PureComponent } from 'react';
import queryString from 'query-string/index';
import BasicLayout from '../../layouts/BasicLayout';
import { connect } from 'react-redux';
import { actions as termActions, selectors as termSelectors } from '../../../reducers/terms';
import ItemChanges from './ItemChanges';
import { css } from 'emotion';
import { fetchLatestTermRevision } from '../../../services/PricingTerm';
import TermStatus from '../../../constants/termStatus';
import CommentsWrapper from '../../common/CommentsWrapper';
import { Select } from '@cimpress/react-components';
import TimeAgo from 'react-timeago';
import TermStatusLabel from './TermStatusLabel';
import TermLink from '../../common/TermLink';
import TermActions from '../viewTermRevision/TermActions';
import moment from 'moment/moment';
import InfoChanges from './InfoChanges';
import { Snackbar } from '@cimpress/react-components';
import { extractV2KeyInfo, productAgreementUrl } from '../../../utilities/productAgreementHelper';

const divStyle = css`
  display: flex;
  flex-direction: column;
`;

const sectionTitleStyle = css`
  border-bottom: 1px solid lightgray;
  margin-bottom: 15px;
  padding-bottom: 5px;
`;

class ContractChangesPage extends PureComponent {
  static defaultProps = {};

  static propTypes = {};

  state = {
    isLatestRevisionLoading: true,
    latestRevisionId: null
  };

  componentDidMount() {
    const { termId } = this.getFromAndTo(this.props, this.state);
    this.props.fetchAllTermRevisions(termId);
    this.loadLatestTermRevision(termId);
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const {
      termId: prevTermId,
      fromRevisionId: prevFromRevisionId,
      toRevisionId: prevToRevisionId
    } = this.getFromAndTo(prevProps, prevState);
    const { termId, fromRevisionId, toRevisionId } = this.getFromAndTo(this.props, this.state);

    if (prevTermId !== termId || prevFromRevisionId !== fromRevisionId || prevToRevisionId !== toRevisionId) {
      if (fromRevisionId && toRevisionId) {
        this.props.fetchTermRevision(termId, fromRevisionId);
        this.props.fetchTermRevision(termId, toRevisionId);
      }
    }
  }

  getFromAndTo = (props, state) => {
    const { location, match, getAllTermRevisions } = props;
    const { id: termId } = match.params;
    const { search } = location;
    const qs = queryString.parse(search);
    let { from: fromRevisionId, to: toRevisionId } = qs;

    toRevisionId = toRevisionId || state.latestRevisionId;
    const allTermRevisions = getAllTermRevisions(termId);

    if (!fromRevisionId && allTermRevisions && allTermRevisions.length) {
      // get all term revisions in reverse order
      const reversed = allTermRevisions.slice().reverse();
      const startIdx = reversed.findIndex(tr => '' + tr.revisionId === toRevisionId);
      const termsToConsider = reversed.slice(startIdx);
      const fromRevision = termsToConsider.find(
        tr => '' + tr.revisionId !== toRevisionId && tr.status !== TermStatus.PENDING
      );
      fromRevisionId = fromRevision ? fromRevision.revisionId : reversed[allTermRevisions.length - 1].revisionId;
    }

    return { termId, fromRevisionId, toRevisionId };
  };

  loadLatestTermRevision = termId => {
    this.setState({ isLatestRevisionLoading: true });
    return fetchLatestTermRevision(termId).then(lt => {
      this.setState({
        latestRevisionId: lt.revisionId,
        isLatestRevisionLoading: false
      });

      return lt;
    });
  };

  onChangeFromRevisionId = selected => {
    let currentUrlParams = new URLSearchParams(window.location.search);
    currentUrlParams.set('from', selected.value);
    this.props.history.push(window.location.pathname + '?' + currentUrlParams.toString());
  };

  onChangeToRevisionId = selected => {
    let currentUrlParams = new URLSearchParams(window.location.search);
    currentUrlParams.set('to', selected.value);
    this.props.history.push(window.location.pathname + '?' + currentUrlParams.toString());
  };

  onStatusUpdated = () => {
    const { termId, toRevisionId } = this.getFromAndTo(this.props, this.state);
    const { latestRevisionId: previousLatestRevisionId } = this.state;
    this.loadLatestTermRevision(termId).then(lt => {
      if (toRevisionId === previousLatestRevisionId.toString()) {
        this.onChangeToRevisionId({ value: lt.revisionId });
      }
    });
  };

  revisionValueRenderer = ({ term, isLatest }) => {
    return (
      <span>
        Revision {term.revisionId} {isLatest && '(latest)'}
      </span>
    );
  };

  revisionOptionRenderer = ({ term, isLatest }) => {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Revision {term.revisionId} {isLatest && '(latest)'}
          <TermStatusLabel status={term.status} />
        </div>
        <div>
          <small>
            Created{' '}
            <b>
              <TimeAgo date={moment.utc(term.createdAt)} />
            </b>
          </small>
        </div>
        <div>
          <small>
            by <b>{term.createdByEmail}</b>
          </small>
        </div>
      </div>
    );
  };

  render() {
    const { termId, fromRevisionId, toRevisionId } = this.getFromAndTo(this.props, this.state);
    const { getAllTermRevisions, getSkuRuleSetLoadingStatus } = this.props;
    const { latestRevisionId } = this.state;
    const allTermRevisions = getAllTermRevisions(termId);

    const fromOptions = allTermRevisions.map(t => ({
      label: t.revisionId.toString(),
      value: t.revisionId.toString(),
      term: t,
      disabled: t.revisionId > toRevisionId,
      isLatest: latestRevisionId === t.revisionId
    }));
    const toOptions = allTermRevisions.map(t => ({
      label: t.revisionId.toString(),
      value: t.revisionId.toString(),
      term: t,
      disabled: t.revisionId < fromRevisionId,
      isLatest: latestRevisionId === t.revisionId
    }));

    fromOptions.sort((a, b) => b.value - a.value);
    toOptions.sort((a, b) => b.value - a.value);

    const from = this.props.getTermRevision(termId, fromRevisionId);
    const to = this.props.getTermRevision(termId, toRevisionId);
    const v2Key = this.props.match.params.v2Key;
    const v2Info = extractV2KeyInfo(v2Key);
    return (
      <div>
        <div style={{ paddingLeft: '20px' }}>
          {v2Info.businessIds && v2Info.businessIds !== 'undefined' ? (
            <a href={productAgreementUrl(v2Info)}>Back to Product Agreements</a>
          ) : null}
        </div>
        <BasicLayout className={divStyle}>
          {getSkuRuleSetLoadingStatus &&
            getSkuRuleSetLoadingStatus.isError && (
              <div>
                <Snackbar show={getSkuRuleSetLoadingStatus.isError} bsStyle="danger" delay={2000}>
                  Ruleset failed to load
                </Snackbar>
              </div>
            )}

          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <h4 style={{ marginBottom: '24px' }}>Compare</h4>
            <div style={{ margin: '0 20px', position: 'relative' }}>
              <Select
                label="New Revision"
                value={'' + toRevisionId}
                options={toOptions}
                onChange={this.onChangeToRevisionId}
                optionRenderer={this.revisionOptionRenderer}
                valueRenderer={this.revisionValueRenderer}
                clearable={false}
              />
              {to && (
                <small style={{ position: 'absolute', bottom: '-2px' }}>
                  <TermLink term={to} linkLabel="View this revision" v2Key={v2Key} v2Info={v2Info} />
                </small>
              )}
            </div>
            <h4 style={{ marginBottom: '24px' }}>with</h4>
            <div style={{ margin: '0 20px', position: 'relative' }}>
              <Select
                label="Old Revision"
                value={'' + fromRevisionId}
                options={fromOptions}
                onChange={this.onChangeFromRevisionId}
                optionRenderer={this.revisionOptionRenderer}
                valueRenderer={this.revisionValueRenderer}
                clearable={false}
              />
              {from && (
                <small style={{ position: 'absolute', bottom: '-2px' }}>
                  <TermLink term={from} linkLabel="View this revision" v2Key={v2Key} v2Info={v2Info} />
                </small>
              )}
            </div>
          </div>
          <hr style={{ width: '100%' }} />
          {from &&
            to && (
              <div>
                <h5 className={sectionTitleStyle}>
                  Contract Information <small>(Changes are bolded)</small>
                </h5>
                <InfoChanges from={from} to={to} />

                <h5 className={sectionTitleStyle}>SKUs</h5>
                <ItemChanges from={from} to={to} />
                <div style={{ height: '20px' }} />
                {to.revisionId === latestRevisionId && (
                  <div className="col-xs-12">
                    <TermActions termRevision={to} onStatusUpdated={this.onStatusUpdated} />
                  </div>
                )}
                <CommentsWrapper termId={termId} />
              </div>
            )}
        </BasicLayout>
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  return {
    getTermRevision: (termId, revisionId) => termSelectors.getTermRevisionWithMoreInfo(state, termId, revisionId),
    isPageLoading: termSelectors.getIsViewTermRevisionPageLoading(state),
    pageLoadingFailed: termSelectors.getHasViewTermRevisionPageLoadingFailed(state),
    getAllTermRevisions: termId => termSelectors.getAllRevisions(state, termId),
    getSkuRuleSetLoadingStatus: termSelectors.getSkuRuleSetLoadingStatus(state)
  };
};

const mapDispatchToProps = {
  fetchTermRevision: termActions.fetchTermRevision,
  fetchAllTermRevisions: termActions.fetchAllTermRevisions,
  resetFetchTermRevisionStatus: termActions.resetFetchTermRevisionStatus
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ContractChangesPage);
